/* ---------------------------------------------------------------------------
   DPWH Road & Bridge Inventory values for 43 of the 108, by way of OpenStreetMap.

   PROVENANCE — READ THIS BEFORE TRUSTING A COORDINATE.

   A SECOND SOURCE, KEPT IN A SECOND FILE. src/data/dpwh.ts is the March 2026
   market-sounding pack and carries six facts per structure. This is a different
   source of different quality covering a different subset, and merging the two
   tables into one would have destroyed the only thing that makes either
   quotable: which of them a given value came from. `toDpwhBridge` joins them at
   the end; nothing else does.

   WHERE IT COMES FROM. OpenStreetMap, queried through Overpass on 2026-09-02 for
   every way whose `ref` matches a bridge id in this register. 39 of the 43 carry
   `source=DPWH-RBI` (or `RBI-DPWH`, or `DPWH:RBI` — the taggers were not
   consistent), meaning they were imported from DPWH's own Road & Bridge
   Inventory. Four — B00917MN, B01744MN, B01798MN, B01942MN — carry no source
   tag at all, so for those the honest statement is "OpenStreetMap, keyed to
   DPWH's bridge id" and nothing stronger.

   THE JOIN IS ON DPWH'S OWN PRIMARY KEY, not on a name. `ref` holds the
   `BRIDGE_ID` this register already stores — B00373SM, B00894MN, B02595LZ — so
   this is a key join and not a fuzzy match. That matters because a name match
   would not have worked at all: OSM names the bridge ways along the Maharlika
   Highway in Samar after the ROAD, forty of them "Maharlika Highway", and none
   of Pupua, Antiao, Jiabong or Motiong appears as a bridge name anywhere.

   FIVE INDEPENDENT CHECKS, and they are the reason this file exists rather than
   a note saying coordinates could not be found:

     1. **The key.** 43 exact matches out of 108.
     2. **The name.** All 43 corroborate the pack's own name, including the
        awkward ones — "Calbiga Overpass", "Bolo Bridge № 2", "Langkila-an"
        against the pack's "Langkilaan".
     3. **The stations.** The pack knows stations and no positions; OSM knows
        positions and no stations. Ordering the matched structures by station and
        measuring the ground distance between consecutive pairs gives a
        straight-line-to-along-station ratio of 0.83–1.03 with a median around
        0.92 — which is what road sinuosity looks like, and is impossible if the
        join were matching the wrong structures. ZERO gaps over 500 m came out
        inconsistent. `npm test` re-runs this.
     4. **The length.** Both sources carry it. 34 of 40 agree to within
        rounding, five more within 3 m, and one disagrees — see the conflict
        note below.
     5. **The office.** 24 records name a District Engineering Office, and all
        24 confirm the package-to-district mapping in src/data/dpwh.ts. None
        contradicts it.

   WHAT IT IS WORTH. These are OSM way centres: a volunteer's tracing of a
   structure whose id came from DPWH, not a surveyed position and not the
   centreline of an abutment. Good enough to put a marker on a map, find the
   crossing, and see the register's structures in the right order along the right
   highway. That is the same worth the Nepal register's coordinates have, and it
   is marked on screen — see `INVENTORY_IMPORT` in src/data/provenance.ts, which
   puts an `R` beside every value that came from here.

   THE ONE CONFLICT, recorded rather than resolved. PNR Overpass (B00435LZ) is
   22.30 m in the market-sounding pack and 39.00 m in the inventory. Two DPWH
   sources disagreeing about one structure. **The pack wins**, because it is this
   register's stated source of record for length and because overwriting a
   sourced figure with another sourced figure silently is how a register stops
   being auditable. `rbiLength` below keeps the other number so the disagreement
   is visible, and `npm test` asserts it is still the only one.

   WHAT IS DELIBERATELY NOT TAKEN FROM HERE:

     · **`length`.** The pack's, always. See the conflict above.
     · **`carriageway`.** The inventory has one width and does not say whether it
        is the deck or the carriageway. It lands in `deckWidth`, which is the
        wider of the two readings and therefore the conservative place to put
        it; inventing a carriageway width from it would be arithmetic on an
        assumption.
     · **`spans`, `deckRL`, `hfl`, `designLoad`, `bearings`, foundations.** Not
        in the OSM tagging. Still not recorded, still null.
     · **Anything for the other 65 records.** They have no match, so they get
        nothing. Samar is missing 17 of 34, Agusan del Sur 35 of 52,
        Quirino-Andaya 13 of 22.

   LICENCE. OpenStreetMap data is ODbL. This file is a derived database and
   carries that obligation; the attribution belongs with any publication of the
   register, alongside the tile credits in src/map/nepalMap.ts.

   THE BETTER SOURCE STILL EXISTS AND IS STILL WORTH GETTING. DPWH publishes the
   Road & Bridge Inventory and a Detailed Bridge Inventory itself
   (dpwh.gov.ph/dpwh/gis/dbi, over an ArcGIS service at apps.dpwh.gov.ph), keyed
   on this same bridge id, covering all 108 rather than 43, and with spans,
   design load and foundations that OSM does not carry. It is behind a WAF that
   refuses automated clients, so it needs a person with a browser or a data
   request to DPWH. When that arrives it should REPLACE this file rather than
   being merged into it — see `GR-33` in ROADMAP.md.
   --------------------------------------------------------------------------- */

/**
 * One structure's inventory values, in the order the columns read below.
 *
 * A tuple table for the same reason src/data/dpwh.ts uses one: 43 rows on 43
 * lines, where a transcription error shows up as a misaligned column. Nothing
 * here is hand-typed — it is generated from the Overpass response — but it is
 * reviewed in a diff like everything else.
 */
type RbiRow = readonly [
  id: string,
  /** The OSM way this came from, so any value can be traced back to it. */
  osmWay: string,
  lat: number,
  lng: number,
  /** OSM `width`, from the inventory's BR_WIDTH. Metres. */
  widthM: number | null,
  /** OSM `start_date`, from the inventory's ACTUAL_YR. */
  year: number | null,
  /** `material` and `bridge:structure` composed — "Reinforced concrete beam". */
  superstructure: string | null,
  /** The District Engineering Office, with " Engineering Office" trimmed. */
  district: string | null,
  /** The province in that office's name. A derivation, not a source field. */
  province: string | null,
  barangay: string | null,
  municipality: string | null,
  /**
   * The inventory's own length, kept ONLY so a disagreement with the pack is
   * visible. Never written to `Bridge.length`.
   */
  rbiLength: number | null,
];

const RBI_ROWS: readonly RbiRow[] = [
  ['B00381SM', 'way/954523476', 11.7025501, 125.0680435, 8.8, 1970, 'Reinforced concrete beam', 'Samar 2nd District', 'Samar', 'Malihao', 'Hinabangan', 11.0],
  ['B00382SM', 'way/954523477', 11.7004963, 125.0666521, 7.4, 1970, 'Reinforced concrete beam', 'Samar 2nd District', 'Samar', 'Rawis', 'Hinabangan', 25.0],
  ['B00383SM', 'way/954523480', 11.6908158, 125.0517897, 8.92, 1970, 'Steel', 'Samar 2nd District', 'Samar', 'Canduyucan', 'San Sebastian', 32.0],
  ['B00385SM', 'way/1533484664', 11.6470359, 125.018912, 8.25, 1970, 'Reinforced concrete', 'Samar 2nd District', 'Samar', 'Canticum', 'Calbiga', 35.0],
  ['B00386SM', 'way/1533490124', 11.6319763, 125.021119, 9.25, 1970, 'Steel', 'Samar 2nd District', 'Samar', 'Polangi', 'Calbiga', 28.0],
  ['B00387SM', 'way/1533490120', 11.6282142, 125.0183844, 8.9, 1970, 'Steel', 'Samar 2nd District', 'Samar', 'Polangi', 'Calbiga', 106.0],
  ['B00388SM', 'way/1533490121', 11.6278789, 125.017592, 9.45, 1970, 'Reinforced concrete', 'Samar 2nd District', 'Samar', 'Poblacion 6', 'Calbiga', 14.0],
  ['B00392SM', 'way/1531634357', 11.5861367, 124.9913758, 8.8, 1970, 'Steel', 'Samar 2nd District', 'Samar', 'Dolores', 'Pinabacdao', 18.0],
  ['B00394SM', 'way/1531634358', 11.567064, 125.003509, 8.87, 1970, 'Steel', 'Samar 2nd District', 'Samar', 'Lale', 'Pinabacdao', 24.0],
  ['B00395SM', 'way/1531630467', 11.5474777, 125.0179614, 8.85, 1970, 'Steel', 'Samar 2nd District', 'Samar', 'Antol', 'Pinabacdao', 28.0],
  ['B00396SM', 'way/1380996159', 11.510542, 125.0262194, 8.8, 1970, 'Reinforced concrete beam', 'Samar 2nd District', 'Samar', 'Parasanon', 'Pinabacdao', 25.0],
  ['B00398SM', 'way/954116213', 11.4389661, 125.0154932, 8.85, 1970, 'Steel', 'Samar 2nd District', 'Samar', 'Manunca', 'Santa Rita', 24.0],
  ['B00399SM', 'way/1005751447', 11.4294113, 125.0130467, 9.55, 2003, 'Concrete', 'Samar 2nd District', 'Samar', 'Lupig', 'Santa Rita', 21.0],
  ['B00401SM', 'way/1531242690', 11.4084853, 124.9967835, 9.5, 1970, 'Reinforced concrete', 'Samar 2nd District', 'Samar', 'San Pascual', 'Santa Rita', 23.0],
  ['B00402SM', 'way/1531242695', 11.39837, 124.9999178, 8.84, 1970, 'Steel', 'Samar 2nd District', 'Samar', 'Bago Libas', 'Santa Rita', 18.0],
  ['B00403SM', 'way/1005504646', 11.3666494, 125.0240889, 9.12, 1970, 'Steel', 'Samar 2nd District', 'Samar', 'Pagsulhugon', 'Sta Rita', 32.0],
  ['B00404SM', 'way/1005504643', 11.3638592, 125.0252332, 8.82, 1970, 'Concrete', 'Samar 2nd District', 'Samar', 'Magsaysay', 'Sta. Rita', 30.0],
  ['B00894MN', 'way/1035920509', 8.8718053, 125.6563178, 16.2, 1958, 'Reinforced concrete beam', 'Agusan del Sur 1st District', 'Agusan del Sur', 'Afga', 'Sibagat', 24.0],
  ['B00895MN', 'way/892542226', 8.8184757, 125.6944835, 16.68, 1988, 'Steel truss', 'Agusan del Sur 1st District', 'Agusan del Sur', 'Poblacion', 'Sibagat', 36.0],
  ['B00898MN', 'way/1540071820', 8.7614054, 125.7153909, 19.9, 1971, 'Reinforced concrete', 'Agusan del Sur 1st District', 'Agusan del Sur', 'Canayugan', 'Bayugan', 22.0],
  ['B00900MN', 'way/1540075599', 8.7309135, 125.7297324, 9.5, 2002, 'Reinforced concrete', 'Agusan del Sur 1st District', 'Agusan del Sur', 'Maygatasan', 'Bayugan', 179.0],
  ['B00901MN', 'way/1529943772', 8.7136736, 125.7543415, 23.05, 2002, 'Reinforced concrete beam', null, null, null, null, 22.0],
  ['B00902MN', 'way/1529947348', 8.7115044, 125.7706975, 16.65, 1957, 'Reinforced concrete beam', null, null, null, null, 48.0],
  ['B00903MN', 'way/1529947317', 8.7116561, 125.792101, 16.61, 2002, 'Reinforced concrete beam', null, null, null, null, 20.0],
  ['B00904MN', 'way/1529947352', 8.7115399, 125.7927639, 17.09, 2002, 'Reinforced concrete beam', null, null, null, null, 60.0],
  ['B00905MN', 'way/1529948426', 8.711083, 125.7953588, 17.85, 2002, 'Reinforced concrete beam', null, null, null, null, 27.0],
  ['B00906MN', 'way/1529948560', 8.7000221, 125.8166832, 9.65, 2002, 'Reinforced concrete beam', null, null, null, null, 19.0],
  ['B01744MN', 'way/965338408', 8.5119723, 125.970048, null, null, 'Beam', null, null, null, null, null],
  ['B01798MN', 'way/965338409', 8.5112906, 125.9708047, null, null, 'Beam', null, null, null, null, null],
  ['B00917MN', 'way/992966107', 8.4319865, 125.9803921, null, null, null, null, null, null, null, null],
  ['B00899MN', 'way/1514645553', 8.3730119, 126.0065389, 7.8, 1958, 'Reinforced concrete beam', null, null, null, null, 76.0],
  ['B01942MN', 'way/1374309029', 8.298318, 126.0040281, 9.6, 2017, 'Reinforced concrete beam', 'Agusan del Sur 2nd District', 'Agusan del Sur', 'Bayugan 3', 'Rosario', 22.0],
  ['B00931MN', 'way/1313439406', 7.9658569, 126.086234, 9.5, 1990, 'Steel beam', 'Agusan del Sur 2nd District', 'Agusan del Sur', 'Langkilaan', 'Trento', 42.0],
  ['B01966MN', 'way/1313439402', 7.9658571, 126.0861268, 9.53, 2018, 'Reinforced concrete beam', 'Agusan del Sur 2nd District', 'Agusan del Sur', 'Langkilaan', 'Trento', 41.0],
  ['B02595LZ', 'way/1007895095', 14.0642123, 122.3895336, 8.95, 1975, 'Concrete', 'Quezon 4th District', 'Quezon', 'San Luis', 'Guinayangan', 23.0],
  ['B02598LZ', 'way/1420486954', 14.0352124, 122.4398941, 9.2, 1970, 'Reinforced concrete', 'Quezon 4th District', 'Quezon', 'Sto. Niño', 'Tagkawayan', 24.0],
  ['B00446LZ', 'way/1413561860', 13.9199914, 122.6036687, 9.7, 1995, 'Reinforced concrete', 'Camarines Sur 1st District', 'Camarines Sur', 'Cabasag', 'Del Gallego', 138.0],
  ['B00440LZ', 'way/1413563910', 13.8191125, 122.7511375, 9.21, 1992, 'Reinforced concrete', 'Camarines Sur 1st District', 'Camarines Sur', 'Lower Binahan', 'Ragay', 30.0],
  ['B00439LZ', 'way/1413563909', 13.8187656, 122.7555929, 9.84, 1992, 'Reinforced concrete', 'Camarines Sur 1st District', 'Camarines Sur', 'Lower Binahan', 'Ragay', 37.0],
  ['B00437LZ', 'way/1396793620', 13.8208125, 122.794432, 9.7, 1992, 'Reinforced concrete', 'Camarines Sur 1st District', 'Camarines Sur', 'Lower Santa Cruz', 'Ragay', 101.0],
  ['B00436LZ', 'way/1413856979', 13.7933229, 122.8107088, 19.72, 1992, 'Reinforced concrete', 'Camarines Sur 1st District', 'Camarines Sur', 'Agrupacion', 'Ragay', 30.0],
  ['B00435LZ', 'way/1413869115', 13.7827798, 122.8856981, 9.2, 1992, 'Reinforced concrete', 'Camarines Sur 1st District', 'Camarines Sur', 'Colacling', 'Lupi', 39.0],
  ['B00434LZ', 'way/1413869114', 13.7825403, 122.8866723, 9.8, 1992, 'Reinforced concrete', 'Camarines Sur 1st District', 'Camarines Sur', 'Bulawan', 'Lupi', 84.0],];

/** What the inventory adds to one record. Null fields are absent, not zero. */
export interface RbiValues {
  osmWay: string;
  lat: number;
  lng: number;
  deckWidth: number | null;
  year: number | null;
  type: string | null;
  district: string | null;
  province: string | null;
  barangay: string | null;
  municipality: string | null;
  rbiLength: number | null;
}

const BY_ID = new Map<string, RbiValues>(
  RBI_ROWS.map((r) => [
    r[0],
    {
      osmWay: r[1],
      lat: r[2],
      lng: r[3],
      deckWidth: r[4],
      year: r[5],
      type: r[6],
      district: r[7],
      province: r[8],
      barangay: r[9],
      municipality: r[10],
      rbiLength: r[11],
    },
  ]),
);

/** The inventory's values for one bridge id, or null where it has no match. */
export function rbiFor(bridgeId: string): RbiValues | null {
  return BY_ID.get(bridgeId) ?? null;
}

/** Every bridge id the inventory covers. 43 of the register's 108. */
export function rbiIds(): string[] {
  return [...BY_ID.keys()];
}

/**
 * Which `Bridge` fields a value can arrive in from here.
 *
 * Read by src/data/provenance.ts to decide which values get the `R` mark, and
 * by `npm test` to check that nothing else is silently taken from this file. It
 * is the list this module is allowed to touch, not a list of what it filled —
 * a record with no recorded year still has none.
 */
export const RBI_FIELDS = [
  'lat',
  'lng',
  'deckWidth',
  'year',
  'type',
  'district',
  'province',
] as const;
